import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import LikeButton from "../components/LikeButton";
import DownloadButton from "../components/DownloadButton";
import SaveButton from "../components/SaveButton";
import ShareButton from "../components/ShareButton";
import Comments from "../components/Comments";
import ImageLightbox from "../components/ImageLightbox";
import ImageCarousel from "../components/ImageCarousel";
import TruncatedText from "../components/TruncatedText";
import { PiChatCircleBold } from "react-icons/pi";
import { IoPricetag } from "react-icons/io5";
import VerifiedBadge from "../components/VerifiedBadge";
import { addRecentlyViewed } from "../utils/recentlyViewed";
import { likeDesign } from "../utils/likes";
import { fetchMyEngagement, subscribeToMyEngagement } from "../utils/myEngagement";
import ReportDesignButton from "../components/ReportDesignButton";
import TagInfoModal from "../components/TagInfoModal";
import { confirmLogin } from "../utils/dialog";
import { useLanguage } from "../context/LanguageContext";
import { checkBlockStatus } from "../utils/blocks";
import { checkMuteStatus } from "../utils/mutes";

export default function DesignView() {
  const { t } = useLanguage();
  const { designId } = useParams();
  const navigate = useNavigate();

  const [design, setDesign] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Every list-based feed (Home, Trending, HashtagPage, Search, Saved)
  // hides blocked/muted users' designs, but a direct link to one (a bookmark,
  // an old share, a comment mention, browser history) bypassed that
  // entirely — the blocked person's design still loaded, and could still be
  // liked/commented/downloaded from here, same as PublicProfile.jsx already
  // guards against for whole profiles.
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(getStoredUser);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  // Two separate small queries rather than one self-referential nested
  // select (designs embedding designs via the same FK in both directions is
  // exactly the kind of PostgREST query that's easy to get subtly wrong) —
  // neither blocks the main design from rendering, so both run fire-and-forget.
  const [inspiredSource, setInspiredSource] = useState(null);
  const [remadeCount, setRemadeCount] = useState(0);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    }
    getUser();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadDesign() {
      setLoading(true);
      // Reset from a previous designId's failed load — without this, once
      // any one design 404s, notFound stays stuck true forever and every
      // later, perfectly valid design on this same mounted page would also
      // render "This design couldn't be found." instead of itself.
      setNotFound(false);
      setBlocked(false);

      const { data, error } = await supabase
        .from("designs")
        .select("*, users(username, avatar_url, verified)")
        .eq("id", designId)
        .maybeSingle();

      // Navigating to another design before this fetch resolved (two fetches
      // now in flight) used to let whichever one resolved LAST win — if that
      // was this now-stale one, the page would show design A's images/owner/
      // download link while the URL and the rest of the app had already
      // moved on to design B.
      if (cancelled) return;

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      let designWithEngagement = data;
      const {
        data: { session }
      } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      if (currentUser) {
        // Neither call depends on the other's result (both only need
        // currentUser.id/data.user_id, known up front) — running them in
        // parallel instead of one after the other saves a full extra
        // round-trip on every single design-page load.
        const [{ likedIds, savedIds }, status] = await Promise.all([
          fetchMyEngagement([data.id], currentUser.id),
          currentUser.id !== data.user_id
            ? checkBlockStatus(currentUser.id, data.user_id)
            : Promise.resolve({ blockedByMe: false, blockedMe: false })
        ]);
        if (cancelled) return;

        if (status.blockedByMe || status.blockedMe) {
          setBlocked(true);
          setLoading(false);
          return;
        }

        designWithEngagement = {
          ...data,
          _likedByMe: likedIds.has(data.id),
          _savedByMe: savedIds.has(data.id)
        };
      }

      if (cancelled) return;

      setDesign(designWithEngagement);
      setLoading(false);
      addRecentlyViewed(data);

      if (data.inspired_by_design_id) {
        supabase
          .from("designs")
          .select("id, title, users(id, username)")
          .eq("id", data.inspired_by_design_id)
          .maybeSingle()
          .then(async ({ data: source }) => {
            if (cancelled || !source) return;
            // A remake can credit a design whose owner the viewer has since
            // blocked or muted — without this check the "Inspired by" line
            // would still surface a link into that person's content, the
            // same class of bypass fixed elsewhere (Home's feed, Comments,
            // NotificationBell) for every other blocked/muted surface.
            if (currentUser && source.users?.id) {
              const [status, muted] = await Promise.all([
                checkBlockStatus(currentUser.id, source.users.id),
                checkMuteStatus(currentUser.id, source.users.id)
              ]);
              if (cancelled) return;
              if (status.blockedByMe || status.blockedMe || muted) return;
            }
            setInspiredSource(source);
          });
      }
      supabase
        .from("designs")
        .select("id", { count: "exact", head: true })
        .eq("inspired_by_design_id", data.id)
        .then(({ count }) => {
          if (!cancelled) setRemadeCount(count || 0);
        });

      // The RPC already dedupes repeat views within 60s for logged-in
      // visitors (via design_engagement_log), but that table requires a
      // real user_id, so it can't do the same for a logged-out visitor —
      // every refresh would otherwise add another view with no dedup at
      // all. A per-tab sessionStorage flag closes that gap for anonymous
      // visitors too, and as a bonus skips the RPC call entirely for
      // logged-in ones who've already been counted this session.
      let alreadyViewed = false;
      try {
        const viewedKey = `viewed_design_${designId}`;
        alreadyViewed = !!sessionStorage.getItem(viewedKey);
        if (!alreadyViewed) sessionStorage.setItem(viewedKey, "1");
      } catch {
        // sessionStorage unavailable (private mode, etc.) — fall through and
        // still count the view rather than block on it.
      }

      if (!alreadyViewed) {
        supabase.rpc("increment_design_view", { target_design_id: designId }).then(({ error }) => {
          if (error) console.log("VIEW COUNT ERROR:", error);
        });
      }
    }

    loadDesign();

    return () => {
      cancelled = true;
    };
  }, [designId]);

  // designId changing (e.g. browser back/forward landing on a different
  // design while this component stays mounted) doesn't unmount the page, so
  // without this a comments panel or lightbox left open from the previous
  // design would pop back open here once the new one finishes loading — the
  // lightbox especially would show the OLD design's cached image list,
  // mismatched with the design now on screen.
  useEffect(() => {
    setCommentsOpen(false);
    setLightboxImage(null);
  }, [designId]);

  // Live updates: only ever one design on this page, so a single filtered
  // listener here is fine (no per-card accumulation risk). comment_count is
  // a trigger-maintained column on designs (updates whenever a comment is
  // inserted/deleted), so this same listener covers likes/downloads and the
  // comment count together. Whether *this viewer* liked/saved it is a
  // separate, per-user concern — see the myEngagement effect below.
  useEffect(() => {
    const channel = supabase
      .channel(`design-view-updates-${designId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "designs", filter: `id=eq.${designId}` },
        payload => {
          setDesign(prev =>
            prev
              ? {
                  ...prev,
                  likes: payload.new.likes,
                  downloads: payload.new.downloads,
                  comment_count: payload.new.comment_count
                }
              : prev
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [designId]);

  // Live updates for this viewer's OWN likes/saves — e.g. liking this design
  // from another tab or device signed into the same account. Filtered to
  // just this user's rows (see utils/myEngagement.js).
  useEffect(() => {
    if (!user) return undefined;

    return subscribeToMyEngagement(user.id, {
      onLike: id => id === designId && updateDesign({ _likedByMe: true }),
      onUnlike: id => id === designId && updateDesign({ _likedByMe: false }),
      onSave: id => id === designId && updateDesign({ _savedByMe: true }),
      onUnsave: id => id === designId && updateDesign({ _savedByMe: false })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, designId]);

  function updateDesign(fields) {
    setDesign(prev => (prev ? { ...prev, ...fields } : prev));
  }

  function toggleComments() {
    setCommentsOpen(prev => !prev);
  }

  function requireLogin(action) {
    if (user) {
      action();
      return;
    }
    confirmLogin(t("common.logInToContinue"), t("nav.login")).then(goLogin => {
      if (goLogin) navigate("/login");
    });
  }

  if (loading) {
    return <p style={mutedText}>Loading…</p>;
  }

  if (notFound) {
    return <p style={mutedText}>This design couldn't be found.</p>;
  }

  if (blocked) {
    return <p style={mutedText}>This design isn't available.</p>;
  }

  const isOwner = user && user.id === design.user_id;

  return (
    <div style={page}>
      <div style={card}>
        <div style={cardHeader}>
          <Link
            to={isOwner ? "/profile" : `/u/${design.user_id}`}
            style={headerLink}
          >
            <img
              src={design.users?.avatar_url || "/default-avatar.svg"}
              alt={`${design.users?.username || "User"}'s avatar`}
              style={cardAvatar}
            />
            <span style={postNameColumn}>
              <span style={usernameLink}>
                @{design.users?.username || "unknown"}
                {design.users?.verified && <VerifiedBadge />}
              </span>
              <PostTime date={design.created_at} />
            </span>
          </Link>
          <div style={headerActions}>
            <button type="button" style={reportBtn} onClick={() => setTagModalOpen(true)} aria-label="Tag info">
              <IoPricetag />
            </button>
            {!isOwner && (
              <ReportDesignButton designId={design.id} reporterId={user?.id} style={reportBtn} />
            )}
          </div>
        </div>

        <ImageCarousel
          images={design.images?.length > 0 ? design.images : [design.image_url]}
          alt={design.title}
          imgStyle={{ ...image, cursor: "zoom-in" }}
          onImageClick={setLightboxImage}
          onDoubleTap={() =>
            likeDesign({ designId: design.id, user, designOwnerId: design.user_id, onUpdate: updateDesign, likeOnly: true })
          }
        />

        <div style={content}>
          <div style={actions}>
            <LikeButton
              designId={design.id}
              likes={design.likes}
              liked={design._likedByMe}
              user={user}
              designOwnerId={design.user_id}
              onUpdate={updateDesign}
            />

            <button
              onClick={() => requireLogin(toggleComments)}
              style={commentBadgeBtn}
              aria-label="Toggle comments"
            >
              <PiChatCircleBold size={27} /> <span style={commentCountText}>{design.comment_count || 0}</span>
            </button>

            <DownloadButton
              fileURL={design.design_url}
              files={design.design_files}
              designId={design.id}
              downloads={design.downloads}
              designOwnerId={design.user_id}
              user={user}
              onUpdate={updateDesign}
            />

            <span style={{ display: "inline-flex" }}>
              <SaveButton
                designId={design.id}
                saved={design._savedByMe}
                user={user}
                onUpdate={updateDesign}
              />
            </span>

            <span style={{ display: "inline-flex", marginLeft: "auto" }}>
              <ShareButton designId={design.id} />
            </span>
          </div>

          <p style={caption}>
            <TruncatedText text={design.title} />
          </p>
          {design.description && (
            <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: "14px" }}>
              {design.description}
            </p>
          )}

          {inspiredSource && (
            <Link to={`/d/${inspiredSource.id}`} style={inspiredByLink}>
              🧵 Inspired by @{inspiredSource.users?.username || "unknown"}'s design
            </Link>
          )}

          {remadeCount > 0 && (
            <button
              type="button"
              style={remadeCountBtn}
              onClick={() => setTagModalOpen(true)}
            >
              🧵 {remadeCount} {remadeCount === 1 ? "person" : "people"} made this
            </button>
          )}

          {tagModalOpen && (
            <TagInfoModal designId={design.id} onClose={() => setTagModalOpen(false)} />
          )}

          {isOwner && <p style={viewsText}>{design.views || 0} views</p>}

          {commentsOpen && (
            <Comments designId={design.id} onClose={toggleComments} designOwnerId={design.user_id} />
          )}
        </div>
      </div>

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette, matches Home/Trending) ===== */

const page = {
  padding: "20px 4px calc(90px + env(safe-area-inset-bottom))",
  background: "var(--bg-page)",
  minHeight: "100vh",
  maxWidth: 480,
  margin: "0 auto",
};

const mutedText = { padding: 20, fontSize: 14, color: "var(--text-muted)", textAlign: "center" };

const card = {
  background: "var(--bg-surface)",
  borderRadius: 14,
  overflow: "hidden",
  border: "1px solid var(--border-soft)",
  boxShadow: "0 4px 14px rgba(59,10,20,0.08)",
};

const cardHeader = { display: "flex", alignItems: "center", gap: 24, padding: "4px 14px" };

const headerActions = { display: "flex", alignItems: "center", gap: 2, marginLeft: "auto", flexShrink: 0 };

const reportBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  border: "none",
  background: "none",
  color: "var(--text-primary)",
  fontSize: 22,
  cursor: "pointer"
};

const headerLink = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
  minWidth: 0,
  textDecoration: "none",
  color: "var(--text-primary)",
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)",
};

const usernameLink = { fontWeight: 700, color: "var(--text-primary)", fontSize: 18 };

const image = { width: "100%", height: "auto", display: "block" };

const content = { padding: "12px 12px 4px" };

const actions = { display: "flex", alignItems: "center", gap: 6, marginBottom: 8 };

const commentBadgeBtn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  borderRadius: 20,
  height: 40,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer",
};

const commentCountText = { fontWeight: "bold", fontSize: 13, color: "var(--text-primary)" };

const caption = { margin: "4px 0", fontSize: 13.5, color: "var(--text-secondary)", whiteSpace: "pre-wrap" };

const viewsText = { margin: "2px 0 0", fontSize: 12, color: "var(--text-muted)" };

const inspiredByLink = {
  display: "block",
  fontSize: 12.5,
  color: "var(--text-muted)",
  textDecoration: "none",
  margin: "6px 0 0"
};

const remadeCountBtn = {
  display: "block",
  fontSize: 12.5,
  color: "var(--text-muted)",
  margin: "8px 0 0",
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  textDecoration: "underline"
};
